import React, { useState, useEffect } from 'react';
import { Select, Button, Spin, Row, Col, Card, Space, message } from 'antd';
import { DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import axios from 'axios';
import { DiffChecker_URL } from '../../Components/Constant';

const { Option } = Select;

export default function Example({
  sourceSelection,
  destinationSelection,
  objectSelection,
  externalIdSelection,
  childRows,
  setChildRows,
  parentCallback,
  childTitlefield,
  setchildTitleField,
  disabled,
  comparejouney
}) {
  const [sourceOrgId, setSourceOrgId] = useState(sourceSelection?.Id || '');
  const [destinationOrgId, setDestinationOrgId] = useState(destinationSelection?.Id || '');
  const [selectedObject, setSelectedObject] = useState(objectSelection[0]?.label || '');
  const [selectedObjectExternalId, setSelectedObjectExternalId] = useState(externalIdSelection[0]?.label);
  const [childObjectList, setChildObjectList] = useState([]);
  const [customFields, setCustomFields] = useState(null);
  const [standardFields, setStandardFields] = useState(null);
  const [loadingFields, setLoadingFields] = useState(false);
  const [titleFieldMap, setTitleFieldMap] = useState(new Map()); // 🗺️ Map: relationshipName -> titleField
  const [unavailableFieldsMap, setUnavailableFieldsMap] = useState(new Map()); // 🗺️ Map: childSObject -> unavailableFields

  // ✅ Fetch fields on mount
  useEffect(() => {
    fetchChildObjects();
  }, []);

  const fetchChildObjects = async () => {
    try {
      setLoadingFields(true);
      const response = await axios.post(`${DiffChecker_URL}/query/fields`, {
        UserId: sourceOrgId,
        ObjectName: selectedObject || comparejouney?.objectConfig?.object,
      });
      setChildObjectList(response.data.childs);
      setLoadingFields(false);
    } catch (error) {
      console.error('Error fetching fields:', error);
      setLoadingFields(false);
    }

  };


  // 🔑 Pre-fill from comparejourney if present
  useEffect(() => {
    if (comparejouney && comparejouney.childData && comparejouney.childData.length > 0) {
      const prefilledRows = comparejouney.childData.map((child, idx) => ({
        id: child.id || Date.now().toString() + idx,
        objectSelectedValue: { childSObject: child.childObject, relationshipName: child.relationshipName, field: child.field }, // include relationshipName
        externalIdSelection: child.externalId || null,
        fieldSelections: child.fields || [],
        titleFieldSelection: child.childTitleField || null, // 🔥 THIS FIXES IT
        fieldOptions: [],         // leave empty, will be filled by selectChildObjectFields
        externalIdOptions: [],    // leave empty, will be filled by selectChildObjectFields
      }));

      setChildRows(prefilledRows);
      notifyParent(prefilledRows);

      // 🔥 Now call selectChildObjectFields for each child row with preserveSelections=true
      prefilledRows.forEach((row, index) => {
        selectChildObjectFields(row.objectSelectedValue, index, true);
      });
    }
  }, [comparejouney, comparejouney?.SourceId]);


  useEffect(() => {
    // ✅ Initialize with one empty row if childRows is empty and no comparejourney
    if ((!childRows || childRows.length === 0)) {
      setChildRows([
        {
          id: Date.now().toString(),
          objectSelectedValue: null,
          externalIdSelection: null,
          externalIdOptions: [],
          fieldSelections: [],
          fieldOptions: [],
        },
      ]);
    }
  }, []);



  const addRow = () => {
    setChildRows((prev) => {
      const newState = [
        ...prev,
        {
          objectSelectedValue: null,
          id: Date.now().toString(),
          externalIdSelection: null,
          externalIdOptions: [],
          fieldSelections: [],
          fieldOptions: [],
        },
      ];
      notifyParent(newState);
      return newState;
    });
  };

  // ✅ Delete row
  const deleteRow = (id) => {
    setChildRows((prev) => {
      const newRows = prev.filter((row) => row.id !== id);
      notifyParent(newRows);
      return newRows;
    });
  };

  // helper: build relationship->titleField map and notify parent
  const notifyParent = (newState, lastSelectedTitle = null) => {
    const mapObj = {};
    newState.forEach((r) => {
      const rel = r?.objectSelectedValue?.relationshipName;
      if (rel && r?.titleFieldSelection) mapObj[rel] = r.titleFieldSelection;
    });
    setTitleFieldMap(new Map(Object.entries(mapObj)));
    parentCallback({ childRows: newState, childTitleField: lastSelectedTitle, childTitleFieldMap: mapObj });
  };

  // ✅ Select title field
  const selectTitleField = (value, index) => {
    console.log('Selected Title Field:', value);
    setChildRows((prev) => {
      const newState = [...prev];
      newState[index].titleFieldSelection = value;
      setchildTitleField(value || 'Record Not Found');
      notifyParent(newState, value || 'Record Not Found');
      return newState;
    });
  };

  // ✅ Select child object
  const selectChildObjectFields = async (value, index, preserveSelections = false) => {
    try {
      const response = await axios.post(`${DiffChecker_URL}/query/fields`, {
        UserId: sourceOrgId,
        ObjectName: value?.childSObject,

      });

      // Fetch unavailable fields for this child object
      let fetchedUnavailableFields = [];
      try {
        console.log('Fetching unavailable fields for', value?.childSObject, sourceOrgId, destinationOrgId);
        const unavailableResponse = await axios.post(`${DiffChecker_URL}/query/unavailableFields`, {
          'SourceUserId': sourceOrgId,
          'TargetUserId': destinationOrgId,
          'ObjectName': value?.childSObject,
        });
        fetchedUnavailableFields = unavailableResponse.data.unavailableFields || [];
        setUnavailableFieldsMap(prev => new Map(prev).set(value?.childSObject, fetchedUnavailableFields));
      } catch (error) {
        console.error('Error fetching unavailable fields:', error);
      }

      const unavailableFieldNames = fetchedUnavailableFields.map(uf => uf?.label || uf?.value || uf);
      setCustomFields(response.data.customFields.filter(f => f !== 'Id' && !unavailableFieldNames.includes(f)));
      setStandardFields(response.data.standardFields.filter(f => f !== 'Id' && !unavailableFieldNames.includes(f)));

      // const optionsForFields = response.data.fields || [];
      const optionsForFields = [...new Set(response.data.fields.filter(f => f !== 'Id' && !unavailableFieldNames.includes(f)) || [])];
      
      console.log('Fetched fields for', value?.childSObject, ':', optionsForFields);
      const optionsForExternalId = response.data.externalIdFields.filter(f => f !== 'Id' && !unavailableFieldNames.includes(f)) || [];

      setChildRows((prev) => {
        const newState = [...prev];
        newState[index] = {
          ...newState[index],
          objectSelectedValue: value,
          fieldOptions: optionsForFields,
          externalIdOptions: optionsForExternalId,
          // Only reset if not preserving (i.e., manual user selection)
          fieldSelections: preserveSelections ? prev[index].fieldSelections : [],
          externalIdSelection: preserveSelections ? prev[index].externalIdSelection : null,
          titleFieldSelection: preserveSelections ? prev[index].titleFieldSelection : null,
        };
        notifyParent(newState);
        return newState;
      });
    } catch (err) {
      console.error('Error fetching child object fields:', err);
    }
  };

  // ✅ Select external ID
  const selectExternalId = (value, index) => {
    setChildRows((prev) => {
      const newState = [...prev];
      newState[index].externalIdSelection = value;
      notifyParent(newState);
      return newState;
    });
  };

  // ✅ Handle field selection
  const handleFieldSelection = (values, index) => {
    setChildRows((prev) => {
      const newState = [...prev];
      let selectedFields = [];

      // Add all custom fields if selected
      if (values.includes('all_custom') && customFields) {
        selectedFields = Array.isArray(customFields) ? [...customFields] : [];
      }

      // Add all standard fields if selected (avoid duplicates)
      if (values.includes('all_standard') && standardFields) {
        const stdFields = Array.isArray(standardFields) ? standardFields : [];
        stdFields.forEach(f => {
          if (!selectedFields.includes(f)) selectedFields.push(f);
        });
      }

      // Add manually selected fields (exclude the special 'all_*' options)
      const manualFields = values.filter(v => v !== 'all_custom' && v !== 'all_standard');
      manualFields.forEach(f => {
        if (!selectedFields.includes(f)) selectedFields.push(f);
      });

      // Append selected title field if present and not already included
      const selectedTitle = newState[index]?.titleFieldSelection;
      if (selectedTitle && !selectedFields.includes(selectedTitle)) {
        selectedFields.push(selectedTitle);
      }

      newState[index].fieldSelections = selectedFields;
      notifyParent(newState);
      return newState;
    });
  };

  return (
    <div>
      <div style={{ margin: '10px',marginTop:0 }}>
        <Button type="primary" onClick={addRow}>
          Add Child
        </Button>
      </div>

      {childRows?.map((row, index) => (
        <Card
          key={row.id}
          style={{ marginBottom: 16, position: 'relative', border: '1px solid #d9d9d9' }}
          bodyStyle={{ padding: '16px' }}
        >
          {/* Delete Icon floating at top-right */}
          <DeleteOutlined
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              fontSize: 18,
              color: 'red',
              cursor: 'pointer',
              zIndex: 10, // 🔑 add this
            }}
            onClick={() => deleteRow(row.id)}
          />

          {/* Row 1: Child Object, External ID, Title Field */}  
          <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <span style={{ marginBottom: 4, display: 'block' }}>Select Child Object</span>
            <Select
              virtual={false}
              showSearch
              style={{ width: '100%' }}
              placeholder="Select Child Object"
              value={row.objectSelectedValue?.childSObject}
              onChange={(value) => {
                const parsedObj = JSON.parse(value);
                selectChildObjectFields(parsedObj, index);
              }}
              options={childObjectList.map((obj, i) => ({
                label: obj.relationshipName || obj.childSObject,
                value: JSON.stringify(obj),
                key: `${obj.childSObject}_${i}`,
              }))}
            />
          </Col>

          {/* External ID */}
          <Col span={8}>
            <span style={{ marginBottom: 4, display: 'block' }}>Select External Id</span>
            <Select
              mode="multiple"
              virtual={false}
              showSearch
              style={{ width: '100%' }}
              placeholder="Select External Id"
              value={row.externalIdSelection}
              onChange={(value) => selectExternalId(value, index)}
            >
              {row?.externalIdOptions?.map((eid) => (
                <Option key={eid} value={eid}>
                  <Space><LinkOutlined />{eid}</Space>
                </Option>
              ))}
            </Select>
          </Col>

          {/* Title Field */}
          <Col span={8}>
            <span style={{ marginBottom: 4, display: 'block' }}>Select Title Field</span>
            <Select
              virtual={false}
              showSearch
              style={{ width: '100%' }}
              placeholder="Select Title Field"
              value={row.titleFieldSelection}
              onChange={(value) => selectTitleField(value, index)}
            >
              {row?.fieldOptions?.map((field) => (
                <Option key={field} value={field}>
                  <Space><LinkOutlined />{field}</Space>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

          {/* Row 2: Fields */}
          <Row>
            <Col span={24}>
              <span style={{ marginBottom: 4, display: 'block' }}>Select Fields</span>
              <Select
                virtual={false}
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                maxTagCount={10} // ✅ show only 10 tags, rest hidden
                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`} // ✅ custom placeholder
                dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                placeholder="Select Fields"
                disabled={disabled}
                value={row.fieldSelections}
                onChange={(values) => handleFieldSelection(values, index)}
              >
                <Option value="all_custom" disabled={loadingFields}>
                  All Custom Fields
                </Option>
                <Option value="all_standard" disabled={loadingFields}>
                  All Standard Fields
                </Option>
                {row?.fieldOptions?.map((field) => (
                  <Option key={field} value={field}>
                    <Space><LinkOutlined />{field}</Space>
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

                  {/* Unavailable Fields Display */}
        {unavailableFieldsMap.get(row?.objectSelectedValue?.childSObject)?.length > 0 && (
          <Row gutter={16} style={{ marginTop: 12, marginBottom: 12 }}>
            <Col span={24}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: '12px', color: '#999' }}>Unavailable Fields (Not in Destination)</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 150, overflowY: 'auto', paddingRight: 8 }}>
                  {(unavailableFieldsMap.get(row?.objectSelectedValue?.childSObject) || []).slice(0, 6).map(f => (
                    <div
                      key={f?.value || f}
                      style={{
                        padding: '2px 8px',
                        backgroundColor: '#fff1f0',
                        border: '1px solid #ff4d4f',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: '11px',
                        color: '#ff4d4f',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <LinkOutlined style={{ fontSize: '9px' }} />
                      {f?.label || f}
                    </div>
                  ))}
                  {(unavailableFieldsMap.get(row?.objectSelectedValue?.childSObject)?.length || 0) > 6 && (
                    <div style={{
                      padding: '2px 8px',
                      backgroundColor: '#f5f5f5',
                      border: '1px dashed #d9d9d9',
                      borderRadius: '12px',
                      fontSize: '11px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      +{(unavailableFieldsMap.get(row?.objectSelectedValue?.childSObject)?.length || 0) - 6} more
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}
        </Card>
      ))}

      {loadingFields && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Spin size="medium" ></Spin>
        </div>
      )}
    </div>
  );
}